import React from 'react';
import clsx from 'clsx';
import {type IconDefinition} from '@fortawesome/fontawesome-common-types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import type {Variant} from '@mui/material/styles/createTypography';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {PrimaryTitle, type PrimaryTitleProps} from '../PrimaryTitle';
import {SecondaryTitle, type SecondaryTitleProps} from '../SecondaryTitle';
import {
	titleClasses,
	getTitleUtilityClass,
	TITLE_CLASS_NAME,
	type TitleClasses,
} from './titleClasses';

type TitleUtiltiyClassProps = WithClasses<TitleClasses, {}>;

const useUtilityClasses = (ownerState: TitleUtiltiyClassProps) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		icon: ['icon'],
		iconArea: ['iconArea'],
		titleArea: ['titleArea'],
	};
	return composeClasses(slots, getTitleUtilityClass, classes);
};

const TitleRoot = styled('div', {
	name: TITLE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	alignItems: 'center',
	minWidth: 0,
}));

const TitleIcon = styled('div', {
	name: TITLE_CLASS_NAME,
	slot: 'Icon',
})(({theme}) => ({
	flex: '0 0 auto',
	borderRadius: '100%',
	background: theme.palette.primary.main,
	height: 42,
	width: 42,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: theme.palette.background.paper,
	fontSize: 18,
	marginRight: 16,
}));

const TitleIconArea = styled('div', {
	name: TITLE_CLASS_NAME,
	slot: 'IconArea',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	alignItems: 'center',
}));

const TitleTitleArea = styled('div', {
	name: TITLE_CLASS_NAME,
	slot: 'TitleArea',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
}));

export interface TitleProps
	extends WithClasses<
		TitleClasses,
		{
			/**
			 * action button to display left of title<br/>
			 * **Note:** if `actionButton` is provided, `icon` will not be displayed
			 */
			actionButton?: React.ReactElement;
			/**
			 * icon to display left of title<br/>
			 * **Note:** if `actionButton` is provided, `icon` will not be displayed
			 */
			icon?: IconDefinition;
			/**
			 * show in loading state
			 */
			loading?: boolean;
			loadingPrimary?: boolean;
			/**
			 * primary title
			 */
			primary: React.ReactNode;
			/**
			 * secondary title
			 */
			secondary?: React.ReactNode;
			/**
			 * show primary title regardless of loading state
			 */
			showPrimaryTitleWhileLoading?: boolean;
			/**
			 * Typography variant to use for the primary title
			 * @default h5
			 */
			variant?: Variant;
			PrimaryTitleProps?: PrimaryTitleProps;
			SecondaryTitleProps?: SecondaryTitleProps;
		}
	> {}

export const TitlePrimitive = ({
	classes: _classes,
	className,
	actionButton,
	icon,
	loading,
	loadingPrimary,
	primary,
	secondary,
	showPrimaryTitleWhileLoading,
	variant = 'h5',
	PrimaryTitleProps,
	SecondaryTitleProps,
	...props
}: TitleProps) => {
	const classes = useUtilityClasses({
		classes: _classes,
	});

	return (
		<TitleRoot className={clsx(classes.root, className)} {...props}>
			{actionButton ? (
				<TitleIconArea>
					<TitleIcon data-testid="title-action" className={classes.icon}>
						{actionButton}
					</TitleIcon>
				</TitleIconArea>
			) : icon ? (
				<TitleIconArea className={classes.iconArea}>
					<TitleIcon data-testid="title-icon" className={classes.icon}>
						<FontAwesomeIcon icon={icon} />
					</TitleIcon>
				</TitleIconArea>
			) : null}
			<TitleTitleArea className={classes.titleArea}>
				<PrimaryTitle
					data-testid="title-primary"
					loading={loadingPrimary || (!showPrimaryTitleWhileLoading && loading)}
					variant={variant}
					{...PrimaryTitleProps}
				>
					{(!showPrimaryTitleWhileLoading && loading) || loadingPrimary
						? undefined
						: primary}
				</PrimaryTitle>
				{secondary != null ? (
					<SecondaryTitle
						data-testid="title-secondary"
						loading={loading}
						{...SecondaryTitleProps}
					>
						{secondary}
					</SecondaryTitle>
				) : null}
			</TitleTitleArea>
		</TitleRoot>
	);
};

/**
 * Title component for unifying the look of titles across the application.
 */
export const Title = React.memo(TitlePrimitive);

export default Title;
